import { z } from "zod";

/** Perfis que o laboratório marca no onboarding; os itens da base guardam os perfis que os sugerem. */
export const PERFIS_DO_LAB = [
  { codigo: "CADCAM", rotulo: "CAD/CAM" },
  { codigo: "CERAMICA", rotulo: "Cerâmica" },
  { codigo: "REMOVIVEL", rotulo: "Removível" },
  { codigo: "IMPLANTE", rotulo: "Implante" },
  { codigo: "IMPRESSAO_3D", rotulo: "Impressão 3D" },
  // "Planning center": quem faz o digital antes da cirurgia ou do tratamento (guias, planejamento, setup).
  { codigo: "PLANEJAMENTO", rotulo: "Planejamento digital" },
  { codigo: "ORTODONTIA", rotulo: "Ortodontia" },
] as const;

/** "GERAL" não é escolha: o item vale pra todo laboratório. */
export const ROTULO_DO_PERFIL: Record<string, string> = {
  ...Object.fromEntries(PERFIS_DO_LAB.map((p) => [p.codigo, p.rotulo])),
  GERAL: "Todo laboratório",
};

export const statusNaBaseSchema = z.enum(["RASCUNHO", "PUBLICADO", "ARQUIVADO"]);
export type StatusNaBase = z.infer<typeof statusNaBaseSchema>;

export const ROTULO_DO_STATUS_NA_BASE: Record<StatusNaBase, string> = {
  RASCUNHO: "Rascunho",
  PUBLICADO: "Publicado",
  ARQUIVADO: "Arquivado",
};

export const ROTULO_DA_FAMILIA: Record<string, string> = {
  CERAMICA_VITREA: "Cerâmica vítrea",
  OXIDO: "Cerâmica de óxido",
  POLIMERO: "Polímero",
  HIBRIDO: "Híbrido",
  METAL: "Metal",
  CONSUMIVEL: "Consumível",
};

/** Famílias do cadastro de materiais (BaseMaterial.familia) — agrupam o cardápio no onboarding. */
export const FAMILIAS_DE_MATERIAL = [
  { codigo: "CERAMICAS", rotulo: "Cerâmicas" },
  { codigo: "RESINAS_3D", rotulo: "Resinas 3D" },
  { codigo: "RESINAS_CONVENCIONAIS", rotulo: "Resinas convencionais" },
  { codigo: "ACRILICOS", rotulo: "Acrílicos" },
  { codigo: "METAIS", rotulo: "Metais" },
  { codigo: "TERMOPLASTICOS", rotulo: "Termoplásticos" },
  { codigo: "POLIMEROS_ESPECIAIS", rotulo: "Polímeros especiais" },
  { codigo: "GESSOS", rotulo: "Gessos" },
  { codigo: "CERAS", rotulo: "Ceras" },
  { codigo: "OUTROS", rotulo: "Outros" },
] as const;
export const ROTULO_DA_FAMILIA_DE_MATERIAL: Record<string, string> = Object.fromEntries(FAMILIAS_DE_MATERIAL.map((f) => [f.codigo, f.rotulo]));

export const ROTULO_DO_TIPO_DE_EQUIPAMENTO: Record<string, string> = {
  SCANNER: "Scanner",
  FRESADORA_SECA: "Fresadora seca",
  FRESADORA_UMIDA: "Fresadora úmida",
  FORNO_SINTERIZACAO: "Forno de sinterização",
  FORNO_CERAMICA: "Forno de cerâmica",
  IMPRESSORA_3D: "Impressora 3D",
  POS_CURA: "Lavagem e pós-cura",
  FUNDICAO: "Fundição",
  POLIMERIZADORA: "Polimerização",
  PLASTIFICADORA: "Plastificadora",
  INJETORA: "Injetora",
  BANCADA: "Bancada",
};

export const TIPOS_NA_BASE = [
  "servicos",
  "materiais",
  "insumos",
  "quimicas",
  "cores",
  "equipamentos",
  "procedimentos",
  "especialidades",
] as const;

/** Abrangência do procedimento da clínica — dente e face são cobrados por dente no plano. */
export const ROTULO_DA_ABRANGENCIA_DO_PROCEDIMENTO: Record<string, string> = {
  DENTE: "Por dente",
  FACE: "Por dente (faces)",
  ARCADA: "Por arcada",
  BOCA: "Boca toda",
};
export const tipoNaBaseSchema = z.enum(TIPOS_NA_BASE);
export type TipoNaBase = z.infer<typeof tipoNaBaseSchema>;

/** O que o laboratório adiciona da base no próprio catálogo (química não: é escolhida, não copiada). */
export const tipoAdicionavelSchema = z.enum(["servicos", "materiais", "insumos", "cores", "equipamentos"]);
export type TipoAdicionavel = z.infer<typeof tipoAdicionavelSchema>;

/** Item que a pessoa criou no onboarding por não achar na base (vira candidato à base depois). */
export const itemProprioSchema = z.object({
  nome: z.string().trim().min(2, "Informe o nome").max(120),
  categoria: z.string().trim().min(1, "Informe a categoria").max(120),
  /** Procedimento da clínica: a especialidade em que ele serve. */
  especialidadeId: z.string().min(1).optional(),
});
export type ItemProprio = z.infer<typeof itemProprioSchema>;

export const concluirOnboardingDoLabSchema = z
  .object({
    perfis: z.array(z.string().min(1)).max(10),
    servicoIds: z.array(z.string().min(1)).max(1000),
    servicosProprios: z.array(itemProprioSchema).max(100).default([]),
    materiaisProprios: z.array(z.string().trim().min(2).max(120)).max(100).default([]),
    /** Serviço → materiais em que ele é feito, já final. Chave: id da base ou "proprio:<nome>". */
    materiaisPorServico: z.record(z.string().min(1), z.array(z.string().min(1)).max(200)).default({}),
    /** Material → códigos de escala de cor (como no Catálogo); ausente ou vazio = toda cor. */
    escalasPorMaterial: z.record(z.string().min(1), z.array(z.string().min(1)).max(20)).default({}),
    /** Onde a pessoa trocou se o dentista marca dente ou arcada. Mesma chave de `materiaisPorServico`. */
    abrangenciaPorServico: z.record(z.string().min(1), z.enum(["DENTE", "ARCADA"])).default({}),
  })
  .refine((v) => v.servicoIds.length + v.servicosProprios.length > 0, { message: "Escolha ao menos um serviço", path: ["servicoIds"] });
export type ConcluirOnboardingDoLab = z.infer<typeof concluirOnboardingDoLabSchema>;

